import { md5 } from "@/lib/md5";
import { api, acct, request } from "./client";
import type { Envelope, LoginResponse } from "./types";

export interface Credentials {
  username: string;
  password: string;
  /** One of these identifies the account. */
  accountName?: string;
  accountRealm?: string;
  phoneNumber?: string;
  accountId?: string;
}

/**
 * Where the token came from. A Kazoo session is the credential form on
 * this console; a portal session was minted by the Vonix portal and knows
 * which cluster to spend itself at.
 */
export type SessionOrigin = "kazoo" | "portal";

export interface Session {
  token: string;
  accountId: string;
  ownerId: string;
  accountName?: string;
  resellerId?: string;
  isReseller: boolean;
  /** Absent on sessions stored before portal sign-in existed; read it through `sessionOrigin`. */
  origin?: SessionOrigin;
  /** Portal sessions only: the Crossbar origin, `/v2` and the rest hang off it. Same-origin when absent. */
  apiOrigin?: string;
  /** Portal sessions only: where blackhole answers. Same-origin `/socket` when absent. */
  socketOrigin?: string;
  /** The token's account is the master account. Superduper is decided from the account document (see auth/operator.ts). */
  isMaster?: boolean;
  /** JWT `exp`, unix seconds, when present. */
  expiresAt?: number;
  /** Space-separated scopes from the JWT, when present. */
  scope?: string;
}

/** "kazoo" is the default: that is all a session could be before the portal. */
export const sessionOrigin = (s?: Pick<Session, "origin">): SessionOrigin => s?.origin ?? "kazoo";

function accountFields(c: Credentials) {
  if (c.accountId) return { account_id: c.accountId };
  if (c.accountRealm) return { account_realm: c.accountRealm };
  if (c.phoneNumber) return { phone_number: c.phoneNumber };
  return { account_name: c.accountName };
}

function decodeJwt(token: string): { exp?: number; scope?: string } {
  try {
    const [, payload] = token.split(".");
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return { exp: typeof json.exp === "number" ? json.exp : undefined, scope: typeof json.scope === "string" ? json.scope : undefined };
  } catch {
    return {};
  }
}

function toSession(env: Envelope<LoginResponse>): Session {
  const token = env.auth_token ?? "";
  const claims = decodeJwt(token);
  return {
    token,
    accountId: env.data.account_id,
    ownerId: env.data.owner_id,
    accountName: env.data.account_name,
    resellerId: env.data.reseller_id,
    isReseller: Boolean(env.data.is_reseller),
    isMaster: Boolean(env.data.is_master_account),
    origin: "kazoo",
    expiresAt: claims.exp,
    scope: claims.scope,
  };
}

/**
 * `PUT /v2/user_auth`. On an MFA-enabled account the first call throws
 * UnauthorizedError(kind = "mfa_required"); call again with `mfaCode`
 * and the same credentials.
 */
export async function login(c: Credentials, mfaCode?: string): Promise<Session> {
  const body: Record<string, unknown> = {
    credentials: md5(`${c.username.toLowerCase()}:${c.password}`),
    method: "md5",
    ...accountFields(c),
  };
  if (mfaCode) body.multi_factor_response = mfaCode;
  const env = await request<Envelope<LoginResponse>>("/user_auth", { method: "PUT", body, anonymous: true, envelope: true });
  return toSession(env);
}

/**
 * Re-hydrate a stored token: `GET /v2/token_auth` answers with the token's
 * claims (account_id, owner_id, ...). Do not use `GET /v2/user_auth/{token}`
 * for this: it reads the legacy token database and 404s on a JWT.
 */
export async function describeToken(token: string): Promise<Session> {
  const env = await request<Envelope<LoginResponse>>("/token_auth", { anonymous: true, envelope: true, headers: { "X-Auth-Token": token } });
  return { ...toSession(env), token, expiresAt: toSession(env).expiresAt ?? decodeJwt(token).exp, scope: decodeJwt(token).scope };
}

export async function requestPasswordReset(username: string, account: Omit<Credentials, "username" | "password">): Promise<void> {
  await api.put("/user_auth/recovery", { username, ui_url: window.location.origin, ...accountFields({ username, password: "", ...account }) }, { anonymous: true });
}

export async function redeemPasswordReset(resetId: string): Promise<void> {
  await api.post("/user_auth/recovery", { reset_id: resetId }, { anonymous: true });
}

/** `PUT .../users/{id}/user_auth {action: impersonate_user}` */
export async function impersonate(accountId: string, userId: string): Promise<Session> {
  const env = await request<Envelope<LoginResponse>>(acct(accountId, "users", userId, "user_auth"), { method: "PUT", action: "impersonate_user", body: {}, envelope: true });
  return toSession(env);
}

/** Only works when `auth_modules.cb_user_auth.renew` is true on the account. */
export async function refreshToken(): Promise<Session> {
  const env = await request<Envelope<LoginResponse>>("/auth", { method: "PUT", action: "refresh_token", body: {}, envelope: true });
  return toSession(env);
}
